import { useMemo } from "react";
import {
  BehaviorSubject,
  distinctUntilChanged,
  map,
  merge,
  shareReplay,
  skip,
  type Observable,
} from "rxjs";
import {
  getSearchParams,
  searchParams$,
  setSearchParams,
} from "../lib/searchParams";
import { useDebouncedCallback } from "./useDebouncedCallback";

interface TypedSearchParamCodec<T> {
  deserializer(v: string): T;
  serializer(v: T): string;
}

export const TypeCodecs = {
  Boolean: {
    deserializer: (v) => v !== "false" && v !== "0",
    serializer: (v: boolean) => (v ? "true" : "false"),
  },
  Date: {
    deserializer: (v: string): Date => new Date(v),
    serializer: (v: Date): string => v.toISOString(),
  },
  String: {
    deserializer: (v) => v,
    serializer: (v: string) => v,
  },
  Number: {
    deserializer: (v) => Number(v),
    serializer: (v: number) => v.toString(10),
  },
} as const satisfies Record<string, TypedSearchParamCodec<unknown>>;

interface TypedSearchParamConfig {
  codec: TypedSearchParamCodec<unknown>;
  name?: string | undefined;
}

export type TypedSearchParams = Record<string, TypedSearchParamConfig>;

type TypeMappingsToResult<T extends TypedSearchParams> = {
  [K in keyof T as K extends string ? `${K}$` : never]: Observable<
    ReturnType<T[K]["codec"]["deserializer"]> | undefined
  >;
} & {
  [K in keyof T as K extends string ? `set${Capitalize<K>}` : never]: (
    value: Parameters<T[K]["codec"]["serializer"]>[0] | undefined,
    replace?: boolean,
  ) => void;
};

export function typedSearchParamsHookFactory(
  dependencies: {
    getSearchParams: () => URLSearchParams;
    searchParams$: Observable<URLSearchParams>;
    setSearchParams: (searchParams: URLSearchParams, replace: boolean) => void;
  } = {
    getSearchParams,
    searchParams$,
    setSearchParams,
  },
  defaultDebounceMs: number,
) {
  return function useTypedSearchParams<T extends TypedSearchParams>(
    typeMappings: T,
    debounceMs = defaultDebounceMs,
  ): TypeMappingsToResult<T> {
    const eagerSearchParams$ = useMemo(
      () => new BehaviorSubject(dependencies.getSearchParams()),
      [],
    );
    const searchParams$ = useMemo(
      () => merge(eagerSearchParams$, dependencies.searchParams$.pipe(skip(1))),
      [eagerSearchParams$],
    );

    const debouncedSetSearchParams = useDebouncedCallback(
      dependencies.setSearchParams,
      debounceMs,
      "cancel",
    );

    const resultValues = useMemo(() => {
      const tmp: [string, Observable<unknown>][] = [];

      for (const [param, typeConfig] of Object.entries(typeMappings)) {
        tmp.push([
          `${param}$`,
          searchParams$.pipe(
            map((v) => v.get(typeConfig.name ?? param)),
            distinctUntilChanged(),
            map((v) => {
              if (v === null) {
                return undefined;
              }

              try {
                return typeMappings[param]!.codec.deserializer(v);
              } catch {
                return undefined;
              }
            }),
            shareReplay({ bufferSize: 1, refCount: true }),
          ),
        ]);
      }

      return tmp;
    }, [searchParams$, typeMappings]);

    const resultSetter = useMemo(() => {
      const tmp: [string, (value: unknown, replace?: boolean) => void][] = [];

      for (const [param, typeConfig] of Object.entries(typeMappings)) {
        tmp.push([
          `set${capitalize(param)}`,
          (
            value: Parameters<(typeof typeConfig)["codec"]["serializer"]>[0],
            replace = true,
          ) => {
            const currentSearchParams = new URLSearchParams(
              eagerSearchParams$.value,
            );

            if (value === undefined) {
              currentSearchParams.delete(typeConfig.name ?? param);
            } else {
              currentSearchParams.set(
                typeConfig.name ?? param,
                typeConfig.codec.serializer(value),
              );
            }

            eagerSearchParams$.next(currentSearchParams);
            debouncedSetSearchParams(currentSearchParams, replace);
          },
        ]);
      }

      return tmp;
    }, [debouncedSetSearchParams, eagerSearchParams$, typeMappings]);

    return useMemo(() => {
      return Object.fromEntries([...resultSetter, ...resultValues]) as any;
    }, [resultSetter, resultValues]);
  };
}

function capitalize(text: string): string {
  if (text.length === 0) {
    return text;
  } else {
    const firstCharacter = text[Symbol.iterator]().next().value;

    return `${firstCharacter!.toUpperCase()}${text.substring(firstCharacter!.length)}`;
  }
}

export const useTypedSearchParams = typedSearchParamsHookFactory(
  undefined,
  500,
);
